import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Check, Quote } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Loader } from "@/components/Loader";
import { ProgressRing } from "@/components/ProgressRing";
import { DayContent } from "@/components/DayContent";
import { PathSwitchNudge } from "@/components/PathSwitchNudge";
import { goldBurst } from "@/lib/confetti";

const QUOTES = [
  "La disciplina è scegliere tra ciò che vuoi ora e ciò che vuoi di più.",
  "Non serve essere grandi per iniziare, ma bisogna iniziare per essere grandi.",
  "Un piccolo passo ogni giorno costruisce una direzione per la vita.",
  "La costanza batte l'intensità: vince chi non si ferma.",
  "Ciò a cui dai attenzione, cresce.",
  "Il modo in cui inizi la giornata definisce come la vivi.",
  "La calma è la nuova forza. Il focus è il nuovo superpotere.",
  "Non contano i giorni perfetti, conta non saltare il giorno dopo.",
];

function greetingText() {
  const h = new Date().getHours();
  if (h < 12) return "Buongiorno";
  if (h < 18) return "Buon pomeriggio";
  return "Buonasera";
}

const todayLabel = () => {
  const t = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  return t.charAt(0).toUpperCase() + t.slice(1);
};

import { CatchupDialog } from "@/components/CatchupDialog";
import { OggiEngine } from "@/components/c2g/OggiEngine";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";

function WeekOver({ calendar, onRestart, nextWeek, week, engine }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const left = calendar.days.filter((d) => d.status !== "done").length;
  const restart = async () => {
    setBusy(true);
    try {
      await api.post("/path/restart");
      toast.success("Nuova settimana iniziata da oggi");
      await onRestart();
    } finally {
      setBusy(false);
    }
  };
  const next = async () => {
    setBusy(true);
    try {
      await api.post("/path/next-week");
      toast.success(`Settimana ${week + 1} iniziata da oggi`);
      await onRestart();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mx-5 mt-5 rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-5" data-testid="week-over-card">
      <p className="text-lg font-semibold text-[#FAF9F6]">La tua settimana si è conclusa {calendar.end_label.toLowerCase()}</p>
      <p className="mt-1 text-sm leading-relaxed text-[#D4D4D8]">
        {engine ? "Hai chiuso la Sera del Giorno 7: il report della settimana è pronto." : left ? `Ti restano ${left} ${left === 1 ? "giorno" : "giorni"} da fare: puoi completarli quando vuoi, oppure ripartire con una nuova settimana.` : "Hai completato tutti i 7 giorni. Vuoi ripartire con una nuova settimana?"}
      </p>
      <button onClick={() => navigate("/riepilogo")} data-testid="week-summary-button" className="btn-gold mt-4 w-full rounded-full px-5 py-3 text-sm font-semibold uppercase tracking-wide">Il tuo riepilogo della settimana</button>
      {nextWeek && (
        <div className="mt-4 rounded-xl border border-[#D4AF37]/30 bg-[#09090B]/40 p-4" data-testid="next-week-box">
          <p className="font-semibold text-[#E5C158]">È pronta la Settimana {week + 1}</p>
          <p className="mt-1 text-sm text-[#D4D4D8]">{week + 1 >= 3 ? "Stessa area, ora in autonomia: 7 nuovi giorni per rendere stabili i cambiamenti e prevenire le ricadute." : "Stessa area, un passo più in profondità: 7 nuovi giorni per consolidare quello che hai iniziato."}</p>
          <button onClick={next} disabled={busy} data-testid="week-next-button" className="btn-gold mt-3 w-full rounded-full px-5 py-3 text-sm font-semibold uppercase tracking-wide disabled:opacity-50">Inizia la Settimana {week + 1}</button>
        </div>
      )}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button onClick={restart} disabled={busy} data-testid="week-restart-button" className={`${nextWeek ? "border border-[#3F3F46] text-[#E4E4E7]" : "btn-gold uppercase tracking-wide"} flex-1 rounded-full px-5 py-3 text-sm font-semibold disabled:opacity-50`}>{nextWeek ? "Ripeti questa settimana" : "Nuova settimana da oggi"}</button>
        <button onClick={() => navigate("/scegli")} data-testid="week-change-path-button" className="flex-1 rounded-full border border-[#3F3F46] px-5 py-3 text-sm font-semibold text-[#E4E4E7]">Scegli un altro percorso</button>
      </div>
    </div>
  );
}

export default function Oggi() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [path, setPath] = useState(null);
  const [day, setDay] = useState(null);
  const [saving, setSaving] = useState(null);
  const times = user?.notif_prefs?.times || { mattino: "08:00", focus: "13:00", sera: "21:00" };

  const load = async () => {
    const { data } = await api.get("/path");
    setPath(data);
    const { data: d } = await api.get(`/path/day/${data.available_day}`);
    setDay({ n: data.available_day, ...d });
  };

  useEffect(() => {
    load().catch((e) => {
      // Abbonato senza profilo/percorso: manda al questionario
      if (e?.response?.status === 400) navigate("/test", { replace: true });
    });
  }, []);

  if (!path || !day) return <Loader label="Preparo la tua giornata…" />;

  if (path.engine) {
    return (
      <AppShell title={`${greetingText()}!`} greeting={todayLabel()}>
        {path.calendar.week_over && <WeekOver calendar={path.calendar} onRestart={load} nextWeek={path.next_week_available} week={path.week} engine />}
        <AnnouncementBanner />
        <OggiEngine path={path} day={day} times={times} reload={load}
          onViewChange={(data) => { setDay({ ...day, ...data }); setPath({ ...path, stats: data.stats, streak: data.stats.days_lived, calendar: data.calendar }); }} />
      </AppShell>
    );
  }

  const progress = day.progress || {};
  const allTasks = ["mattino", "focus", "sera"];
  const totalTasks = allTasks.length;
  const doneCount = allTasks.filter((k) => progress[k]).length;

  const applyResult = (data) => {
    const dp = data.progress[String(day.n)] || {};
    const justCompleted = dp.completed && !progress.completed;
    setDay({ ...day, progress: dp });
    setPath({ ...path, streak: data.streak, available_day: data.available_day, calendar: data.calendar });
    if (justCompleted) {
      if (dp.action_status === "completed") {
        toast.success("Giornata vissuta: ottimo lavoro!");
        goldBurst();
      } else {
        toast("Giornata chiusa. Domani riparti da qui.");
      }
      if (data.available_day !== day.n) setTimeout(() => load(), 1600);
    }
  };

  const toggle = async (key) => {
    setSaving(key);
    try {
      const { data } = await api.post(`/path/day/${day.n}/moment`, { moment: key, done: !progress[key] });
      applyResult(data);
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(null);
    }
  };

  const interact = async (moment, payload) => {
    try {
      const { data } = await api.post(`/path/day/${day.n}/interact`, { moment, data: payload });
      applyResult(data);
      return true;
    } catch {
      toast.error("Errore nel salvataggio");
      return false;
    }
  };

  return (
    <AppShell
      title={`${greetingText()}!`}
      greeting={todayLabel()}
    >
      <CatchupDialog calendar={path.calendar} onDone={load} />
      {path.calendar?.week_over && <WeekOver calendar={path.calendar} onRestart={load} nextWeek={path.next_week_available} week={path.week || 1} />}
      <AnnouncementBanner />
      <div className="px-5 pt-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 flex items-start gap-3 rounded-2xl border border-[#D4AF37]/15 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-4"
          data-testid="daily-quote"
        >
          <Quote size={18} className="mt-1 shrink-0 text-[#D4AF37]" />
          <p className="a2g-read text-lg leading-snug text-[#E8E4D8] font-semibold">{QUOTES[new Date().getDate() % QUOTES.length]}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl border border-[#27272A] bg-[#121215] p-6"
        >
          <span className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-[#D4AF37]/12 blur-3xl" />
          <span className="pointer-events-none absolute -bottom-20 -left-10 h-40 w-40 rounded-full bg-[#D4AF37]/6 blur-3xl" />
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="eyebrow">{day.n === path.calendar?.today_day && !path.calendar?.week_over ? "Il tuo obiettivo di oggi" : `Obiettivo di ${(path.calendar?.days?.[day.n - 1]?.label || "").toLowerCase()}`}</p>
              <h1 className="font-serif-el mt-3 text-3xl font-semibold leading-[1.12] text-[#FAF9F6]">
                {day.day.title}
              </h1>
              <p className="mt-3 a2g-read text-base leading-relaxed text-[#B4B4BC]">{path.profile.obiettivo}</p>
            </div>
            <div className="flex w-24 shrink-0 flex-col items-center rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#18181C] to-[#0D0D0F] px-3 py-4 text-center">
              <Flame size={18} className="text-[#D4AF37]" />
              <span className="tabular-nums font-semibold mt-1 text-3xl font-semibold text-[#FAF9F6]" data-testid="streak-count">
                {path.streak}/7
              </span>
              <span className="mt-1 text-[12px] uppercase leading-tight tracking-wider text-[#8A8E96]">
                Giorni vissuti
              </span>
            </div>
          </div>
        </motion.div>

        <PathSwitchNudge currentDay={day.n} />

        <div className="mt-8 flex items-center justify-between">
          <div>
            <p className="eyebrow" data-testid="oggi-date-day">
              {day.n === path.calendar?.today_day && !path.calendar?.week_over ? `Oggi · ${path.calendar.today_label}` : `Recupero · ${path.calendar?.days?.[day.n - 1]?.label || ""}`}
            </p>
            <h2 className="font-serif-el mt-1 text-2xl font-semibold text-[#FAF9F6]">Tre momenti, una direzione</h2>
          </div>
          <ProgressRing value={doneCount} max={totalTasks} size={56} stroke={5} testid="oggi-progress-ring">
            <span className="tabular-nums font-semibold text-sm font-semibold text-[#FAF9F6]">{doneCount}/{totalTasks}</span>
          </ProgressRing>
        </div>

        <div className="mt-5">
          <DayContent key={day.n} payload={day.day} options={day.options} progress={progress} times={times} saving={saving} onToggle={toggle} onInteract={interact} />
        </div>

        {progress.completed && (
          <div className="mt-6 rounded-2xl border border-[#10B981]/30 bg-[#10B981]/10 p-5 text-center">
            <Check size={26} className="mx-auto text-[#10B981]" />
            <p className="mt-2 text-xl text-[#FAF9F6] font-semibold">Giornata vissuta</p>
            <button
              onClick={() => navigate(day.n === 7 ? "/riepilogo" : "/percorso")}
              data-testid="oggi-go-percorso"
              className="btn-outline-gold mt-3 rounded-full px-5 py-2 text-sm"
            >
              {day.n === 7 ? "Vedi il riepilogo della settimana" : "Vai al percorso"}
            </button>
          </div>
        )}
      </div>
    </AppShell>
  );
}
